// A fenced code block of the agent's reply, with a button that copies it: a macro to paste into the VBA editor is
// 25 lines or more, which no one selects by hand in a narrow pane (sculpt-api docs/plans/PLAN-paste-loops.md,
// decision 25).
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** The plain text of a block as react-markdown hands it: the text nodes under its `pre`, joined. */
export function textOf(node: unknown): string {
  if (!node || typeof node !== 'object') return '';
  const n = node as { type?: string; value?: unknown; children?: unknown[] };
  if (n.type === 'text' && typeof n.value === 'string') return n.value;
  return (n.children ?? []).map(textOf).join('');
}

/** Copy `text`: the Clipboard API, else a hidden selection, which the frames that refuse the API still let through. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // an Office frame may refuse the Clipboard API: fall through
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

export function CodeBlock({ text, children }: { text: string; children: ReactNode }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        className="absolute right-1 top-1 rounded-sm border border-line bg-white px-1.5 py-0.5 text-[11px] text-muted hover:text-ink"
        onClick={async () => {
          if (await copyText(text)) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }
        }}
      >
        {copied ? t('chat.copied') : t('chat.copyCode')}
      </button>
      <pre>{children}</pre>
    </div>
  );
}
